import { ExportType } from '@components'

import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useCsvExport } from '@features/exports/hooks/useCsvExport'
import { useXlsxExport } from '@features/exports/hooks/useXlsxExport'
import { type ExportDocumentFileType } from '@features/exports/types/exportDocumentFileType'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { NotificationKeys } from '../../../enums/notificationKeys'
import { getProductsExportData } from '../utils/getProductsExportData'
import { useFetchAllProducts } from './useFetchAllProducts'
import { useSelectableProductAccounts } from './useSelectableProductAccounts'
import { useTrackProductsExport } from './useTrackProductsExport'

interface UseExportProductsProps {
  enabled?: boolean
}

export const useExportProducts = ({ enabled = true }: UseExportProductsProps = {}) => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const { trackProductsExport } = useTrackProductsExport()

  const isFrenchOrganization = organization?.countryId === 'FR'

  const { products, isLoading: isLoadingProducts } = useFetchAllProducts({ enabled })
  const { accounts, isLoading: isLoadingAccounts } = useSelectableProductAccounts({
    enabled: enabled && isFrenchOrganization,
  })
  const spreadSheetData = useMemo(
    () => getProductsExportData(products, t, isFrenchOrganization ? { accounts } : {}),
    [accounts, isFrenchOrganization, products, t],
  )
  const fileName = t('exports.card.products.title')

  const { exportAsCsv } = useCsvExport({
    spreadSheetData,
    fileName,
    notificationKey: NotificationKeys.ProductsExport,
  })
  const { exportAsXlsx } = useXlsxExport({
    spreadSheetData,
    fileName,
    notificationKey: NotificationKeys.ProductsExport,
  })

  const exportProducts = useCallback(
    (fileType: ExportDocumentFileType) => {
      if (fileType === ExportType.Xlsx) {
        exportAsXlsx()
      } else {
        exportAsCsv()
      }

      trackProductsExport({ fileFormat: fileType, itemCount: products.length })
    },
    [exportAsCsv, exportAsXlsx, products.length, trackProductsExport],
  )

  return useMemo(
    () => ({ exportProducts, isLoading: isLoadingProducts || isLoadingAccounts }),
    [exportProducts, isLoadingAccounts, isLoadingProducts],
  )
}
